import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Form, Spin, message, Radio } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as Icon from 'base/ui/components/Icons';
import PageWrapper from 'pages/Components/PageWrapper';
import CustomButton from 'base/ui/components/Button';
import BoxContainer from 'base/ui/components/BoxContainer';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';
import CustomInput from 'base/ui/components/CustomInput';
import CustomSelect from 'base/ui/components/CustomSelect';
import { wasteProductPresenter, wasteProductStore, commonMasterDataPresenter, commonMasterDataStore } from 'stores/root_store';
import { WasteProductModel } from 'services/RawData/wasteProduct_services';
import { usePermission } from 'hooks/usePermission';
import { SYSTEM_TITLE } from 'utils/constants';
import { removeEmptyValues } from 'utils/transformHelper';
import { customFilterOption } from 'utils/searchHelper';

const createEditWasteProduct = () => {
  return observer(() => {
    const { t } = useTranslation();
    const { id: idParams } = useParams();
    let pageTitle = '';

    if (idParams) {
      usePermission('EDIT');
      pageTitle = t('page-title.update');
      document.title = pageTitle + ' - ' + SYSTEM_TITLE;
    } else {
      usePermission('CREATE');
      pageTitle = t('page-title.create');
      document.title = pageTitle + ' - ' + SYSTEM_TITLE;
    }

    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [wasteGroupOptions, setWasteGroupOptions] = useState<{ label: string; value: string }[]>([]);
    const [wasteGroupingContractOptions, setWasteGroupingContractOptions] = useState<{ label: string; value: string }[]>([]);

    const getDetail = async () => {
      if (idParams) {
        await wasteProductPresenter.getWasteProductById(wasteProductStore, idParams);
        if (wasteProductStore.wasteProductDetailResponse.isSuccess) {
          const detailData = wasteProductStore.wasteProductDetailResponse.data;
          if (detailData) {
            // Kiểm tra xem wasteGroupingEnvironmentPermitId có trong options không
            const storeOptions = commonMasterDataStore.wasteGroupingEnvironmentPermitOptions || [];
            const hasGpmtOption = detailData.wasteGroupingEnvironmentPermitId
              ? storeOptions.some((opt: any) => opt.key === detailData.wasteGroupingEnvironmentPermitId)
              : false;

            // Kiểm tra xem wasteGroupingContractId có trong options không
            const storeContractOptions = commonMasterDataStore.wasteGroupingContractOptions || [];
            const hasContractOption = detailData.wasteGroupingContractId
              ? storeContractOptions.some((opt: any) => opt.key === detailData.wasteGroupingContractId)
              : false;

            // Chỉ set các field có trong options, không set các field không có trong options
            const formValues: any = {
              ...detailData,
            };

            // Chỉ set wasteGroupingEnvironmentPermitId nếu có trong options
            if (hasGpmtOption) {
              formValues.wasteGroupingEnvironmentPermitId = detailData.wasteGroupingEnvironmentPermitId;
            } else {
              // Không set field này vào form nếu không có trong options
              delete formValues.wasteGroupingEnvironmentPermitId;
            }

            // Chỉ set wasteGroupingContractId nếu có trong options
            if (hasContractOption) {
              formValues.wasteGroupingContractId = detailData.wasteGroupingContractId;
            } else {
              // Không set field này vào form nếu không có trong options
              delete formValues.wasteGroupingContractId;
            }

            form.setFieldsValue(formValues);
          }
        }
      }
    };

    const handleSubmit = async (values: any, isContinue?: boolean) => {
      setSaving(true);
      const cleanedValues = removeEmptyValues(values);

      const request: any = {
        productCode: cleanedValues.productCode,
        productName: cleanedValues.productName,
        wasteGroupingEnvironmentPermitId: cleanedValues.wasteGroupingEnvironmentPermitId,
        wasteGroupingContractId: cleanedValues.wasteGroupingContractId,
        actived: cleanedValues.actived !== undefined ? cleanedValues.actived : true,
        isAutoConfirmPlan: cleanedValues.isAutoConfirmPlan !== undefined ? cleanedValues.isAutoConfirmPlan : true,
      };

      Object.keys(request).forEach(key => {
        if (request[key] === undefined) {
          delete request[key];
        }
      });

      if (idParams) {
        const updateRequest = {
          ...request,
          wasteProductId: idParams,
        };
        await wasteProductPresenter.updateWasteProduct(wasteProductStore, updateRequest);
      } else {
        await wasteProductPresenter.createWasteProduct(wasteProductStore, request);
      }

      setSaving(false);
      if (wasteProductStore.crudResponse.isSuccess) {
        message.success(wasteProductStore.crudResponse?.message || (idParams ? t('message.update-success') : t('message.create-success')));
        if (!isContinue) {
          navigate('/MasterData/WasteProduct');
        } else {
          if (!idParams) {
            form.resetFields();
          } else {
            getDetail();
          }
        }
      } else {
        message.error(wasteProductStore.crudResponse?.message || 'Có lỗi xảy ra');
      }
    };

    const loadWasteGroups = async () => {
      await commonMasterDataPresenter.getDropdownWasteGroupingEnvironmentPermit(commonMasterDataStore, true);
      const options = commonMasterDataStore.wasteGroupingEnvironmentPermitOptions || [];
      setWasteGroupOptions(
        options.map((item) => ({
          label: item.value,
          value: item.key,
        })),
      );
    };

    const loadWasteGroupingContracts = async () => {
      await commonMasterDataPresenter.getDropdownWasteGroupingContract(commonMasterDataStore, true);
      const options = commonMasterDataStore.wasteGroupingContractOptions || [];
      setWasteGroupingContractOptions(
        options.map((item) => ({
          label: item.value,
          value: item.key,
        })),
      );
    };

    useEffect(() => {
      Promise.allSettled([loadWasteGroups(), loadWasteGroupingContracts()]).then(() => {
        if (idParams) {
          getDetail();
        }
        setLoading(false);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idParams]);

    return (
      <PageWrapper
        breadcrumb={[{
          text: t('masterdata.waste-product.title'),
          to: '/MasterData/WasteProduct'
        }, {
          text: pageTitle
        }]}
        extras={
          <>
            <CustomButton
              icon={<Icon.Download className="!fill-[#262626]" />}
              disabled={saving}
              loading={saving}
              onClick={() => {

                if (form.getFieldValue('isAutoConfirmPlan') === null || form.getFieldValue('isAutoConfirmPlan') === undefined) {
                  message.error('Vui lòng chọn “Bắt buộc xác nhận kế hoạch bàn giao” trước khi lưu');
                  return;
                }

                form
                  .validateFields()
                  .then((values: WasteProductModel) => {
                    handleSubmit(values);
                  })
                  .catch((errors) => console.log(errors));
              }}
            >
              {t('btn.save')}
            </CustomButton>
            <CustomButton
              type="primary"
              icon={<Icon.Download />}
              disabled={saving}
              loading={saving}
              onClick={() => {
                if (form.getFieldValue('isAutoConfirmPlan') === null || form.getFieldValue('isAutoConfirmPlan') === undefined) {
                  message.error('Vui lòng chọn “Bắt buộc xác nhận kế hoạch bàn giao” trước khi lưu');
                  return;
                }
                form
                  .validateFields()
                  .then((values: WasteProductModel) => {
                    handleSubmit(values, true);
                  })
                  .catch((errors) => console.log(errors));
              }}
            >
              {idParams ? t('btn.save-update') : t('btn.save-continue-create')}
            </CustomButton>
          </>
        }
      >
        <BoxContainer fullHeight>
          <Spin spinning={loading}>
            <div className="h-[70vh]">
              <SearchForm
                layout="vertical"
                autoComplete="off"
                form={form}
                className="gap-x-12"
                column="col-2"
              >
                <SearchItem
                  rules={[{ required: true, message: `${t('message.required-input')} ${t('masterdata.waste-product.product-code')}` }]}
                  name="productCode"
                  label={t('masterdata.waste-product.product-code')}
                >
                  <CustomInput />
                </SearchItem>
                <SearchItem
                  rules={[{ required: true, message: `${t('message.required-input')} ${t('masterdata.waste-product.product-name')}` }]}
                  name="productName"
                  label={t('masterdata.waste-product.product-name')}
                >
                  <CustomInput />
                </SearchItem>
                <SearchItem
                  name="wasteGroupingEnvironmentPermitId"
                  label={t('masterdata.product.waste-group-waste')}
                  rules={[{ required: true, message: `${t('message.required-select')} ${t('masterdata.product.waste-group-waste')}` }]}
                >
                  <CustomSelect
                    options={wasteGroupOptions}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={customFilterOption}
                    placeholder={t('place-holder.select')}
                  />
                </SearchItem>
                <SearchItem
                  name="wasteGroupingContractId"
                  label={t('masterdata.waste-product.waste-subgroup-contract')}
                  rules={[{ required: true, message: `${t('message.required-select')} ${t('masterdata.waste-product.waste-subgroup-contract')}` }]}
                >
                  <CustomSelect
                    options={wasteGroupingContractOptions}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={customFilterOption}
                    placeholder={t('place-holder.select')}
                  />
                </SearchItem>
                {idParams && (
                  <SearchItem name="actived" label={t('masterdata.product.status')} initialValue={true}>
                    <Radio.Group>
                      <Radio value={true}>{t('radio-btn.active')}</Radio>
                      <Radio value={false}>{t('radio-btn.in-active')}</Radio>
                    </Radio.Group>
                  </SearchItem>
                )}

                {/* Bắt buộc xác nhận kế hoạch bàn giao */}
                <SearchItem
                  required
                  name="isAutoConfirmPlan" label={'Bắt buộc xác nhận kế hoạch bàn giao'} initialValue={null}>
                  <Radio.Group>
                    <Radio value={true}>Có</Radio>
                    <Radio value={false}>Không</Radio>
                  </Radio.Group>
                </SearchItem>
              </SearchForm>
            </div>
          </Spin>
        </BoxContainer>
      </PageWrapper>
    );
  });
};

export default createEditWasteProduct;

